import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type Ref,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  type InterventionStatus,
  type Priority,
  type UserRef
} from "@techplanner/shared/core";
import { initials } from "../lib/format";
import { Icon, type IconName } from "./Icon";

/* ---------- Boutons ---------- */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  ref?: Ref<HTMLButtonElement>;
  variant?: "primary" | "accent" | "success" | "danger" | "ghost" | "soft" | "default";
  size?: "small" | "large";
  block?: boolean;
  loading?: boolean;
  icon?: IconName;
};

export function Button({ variant = "default", size, block, loading, icon, children, className = "", disabled, type = "button", ...rest }: ButtonProps) {
  const classes = ["btn", variant !== "default" && variant, size, block && "block", className].filter(Boolean).join(" ");
  return (
    <button type={type} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className="spinner" aria-hidden="true" /> : icon ? <Icon name={icon} size={20} /> : null}
      {children}
    </button>
  );
}

export function IconButton({ icon, label, className = "", ...rest }: { icon: IconName; label: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={`icon-btn ${className}`} aria-label={label} title={label} {...rest}>
      <Icon name={icon} />
    </button>
  );
}

/* ---------- Champs ---------- */

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="field-hint">{hint}</span>
      ) : null}
    </div>
  );
}

export function Switch({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  const id = useId();
  return (
    <div className="switch-row">
      <label htmlFor={id} className="stack-s grow" style={{ gap: 2 }}>
        <span className="strong">{label}</span>
        {hint && <span className="small muted">{hint}</span>}
      </label>
      <span className="switch">
        <input id={id} type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span />
      </span>
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Stepper({
  value,
  min,
  max,
  onChange,
  label,
  editable
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  label: string;
  /** Valeur saisissable au clavier en plus des boutons − / +. */
  editable?: boolean;
}) {
  return (
    <div className="stepper" role="group" aria-label={label}>
      <IconButton icon="minus" label="Moins" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} />
      {editable ? (
        <input
          className="input stepper-input"
          inputMode="numeric"
          aria-label={label}
          value={String(value)}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const n = Number(e.target.value.replace(/\D/g, "") || 0);
            onChange(Math.min(max, Math.max(min, n)));
          }}
        />
      ) : (
        <output aria-live="polite">{value}</output>
      )}
      <IconButton icon="plus" label="Plus" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} />
    </div>
  );
}

/* ---------- Badges et avatars ---------- */

export function StatusBadge({ status }: { status: InterventionStatus }) {
  return (
    <span className={`badge ${status}`}>
      <span className="dot" />
      {STATUS_LABELS[status]}
    </span>
  );
}

export function PriorityBadge({ priority }: { priority: Priority }) {
  if (priority === "normal" || priority === "low") return null;
  return <span className={`badge ${priority}`}>{PRIORITY_LABELS[priority]}</span>;
}

export function Avatar({ user, size }: { user: UserRef; size?: "l" }) {
  return (
    <span className={`avatar ${size ?? ""}`} style={{ ["--avatar" as string]: user.color ?? undefined }} title={user.displayName} aria-label={user.displayName}>
      {initials(user.displayName)}
    </span>
  );
}

export function AvatarStack({ users, max = 3 }: { users: UserRef[]; max?: number }) {
  if (!users.length) return null;
  const shown = users.slice(0, max);
  return (
    <span className="avatar-stack">
      {shown.map((u) => (
        <Avatar key={u.id} user={u} />
      ))}
      {users.length > max && <span className="avatar" style={{ ["--avatar" as string]: "var(--text-3)" }}>+{users.length - max}</span>}
    </span>
  );
}

/* ---------- États ---------- */

export function Empty({ icon = "info", title, children, action }: { icon?: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <Icon name={icon} size={26} />
      </div>
      <strong>{title}</strong>
      {children && <div className="small">{children}</div>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}

export function Skeleton({ height = 72, count = 3 }: { height?: number; count?: number }) {
  return (
    <div className="stack" aria-busy="true" aria-label="Chargement">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "Chargement impossible.";
  return (
    <div className="alert error" role="alert">
      <Icon name="alert" size={20} />
      <div className="grow stack-s">
        <span>{message}</span>
        {onRetry && (
          <div>
            <Button size="small" onClick={onRetry} icon="refresh">
              Réessayer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Alert({ kind = "info", children, icon }: { kind?: "info" | "warning" | "error"; children: ReactNode; icon?: IconName }) {
  return (
    <div className={`alert ${kind}`} role={kind === "error" ? "alert" : undefined}>
      <Icon name={icon ?? (kind === "info" ? "info" : "alert")} size={20} />
      <div className="grow">{children}</div>
    </div>
  );
}

export function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <div className="section-head">
        <h2 className="section-title">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/* ---------- Feuille du bas ---------- */

export function Sheet({
  open,
  title,
  onClose,
  children,
  footer
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Référence stable : le focus n'est placé qu'à l'ouverture, pas à chaque frappe.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeRef.current();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const focusable = ref.current?.querySelector<HTMLElement>("input, textarea, select, button:not([data-close])");
    (focusable ?? ref.current)?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      previous?.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="sheet-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref} tabIndex={-1}>
        <div className="sheet-grip" />
        <div className="sheet-head">
          <h2 id={titleId}>{title}</h2>
          <IconButton icon="close" label="Fermer" onClick={onClose} data-close />
        </div>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/* ---------- Confirmation ---------- */

interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}

const ConfirmContext = createContext<(options: ConfirmOptions) => Promise<boolean>>(async () => false);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback(
    (options: ConfirmOptions) => new Promise<boolean>((resolve) => setState({ ...options, resolve })),
    []
  );
  const close = (value: boolean) => {
    state?.resolve(value);
    setState(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Sheet
        open={state !== null}
        title={state?.title ?? ""}
        onClose={() => close(false)}
        footer={
          <>
            <Button onClick={() => close(false)}>Annuler</Button>
            <Button variant={state?.danger ? "danger" : "primary"} onClick={() => close(true)}>
              {state?.confirmLabel ?? "Confirmer"}
            </Button>
          </>
        }
      >
        {state?.message && <div className="text-2">{state.message}</div>}
      </Sheet>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);

/* ---------- Notifications ---------- */

interface Toast {
  id: number;
  message: string;
  kind: "info" | "success" | "error";
  action?: { label: string; run: () => void };
}

const ToastContext = createContext<(message: string, kind?: Toast["kind"], action?: Toast["action"]) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const show = useCallback((message: string, kind: Toast["kind"] = "info", action?: Toast["action"]) => {
    const id = ++seq.current;
    setToasts((list) => [...list.slice(-2), { id, message, kind, action }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), kind === "error" ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`} role={t.kind === "error" ? "alert" : "status"}>
            {t.kind === "success" && <Icon name="checkCircle" className="toast-icon" size={20} />}
            {t.kind === "error" && <Icon name="alert" size={20} />}
            <span>{t.message}</span>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action!.run();
                  setToasts((list) => list.filter((x) => x.id !== t.id));
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
